import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../services/api';

const TEMPLATE_TYPES = {
  heart: { name: 'Corazón', icon: '❤️' },
  star: { name: 'Estrella', icon: '⭐' },
  circle: { name: 'Círculo', icon: '⭕' }
};

const PhotoBoothMural = () => {
  const { slug, token } = useParams();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [projectInfo, setProjectInfo] = useState(null);
  const [config, setConfig] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  
  const eventSourceRef = useRef(null);
  const isMountedRef = useRef(true);
  const imagesLoadedRef = useRef(new Map());
  const resizeTimeoutRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL 
    ? process.env.REACT_APP_BACKEND_URL.replace(/\/api$/, '')
    : 'http://localhost:3001';

  const loadProjectInfo = useCallback(async () => {
    try {
      const response = token
        ? await publicApi.get(`/photobooth/${slug}/tv/${token}`)
        : await publicApi.get(`/photobooth/${slug}/info`);
      
      if (response.data.success) {
        const info = token ? response.data.projectInfo : response.data.info;
        setProjectInfo(info);
      } else {
        setError(response.data.error || 'Token inválido o expirado');
      }
    } catch (err) {
      setError('Error al cargar el proyecto');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  const loadMuralConfig = useCallback(async () => {
    try {
      const response = await publicApi.get(`/mural/${slug}/config`);
      
      if (response.data.success) {
        setConfig(response.data.config);
      }
    } catch (err) {
      console.error('Error cargando configuración del mural:', err);
    }
  }, [slug]);

  const loadPhotos = useCallback(async () => {
    try {
      const response = await publicApi.get(`/mural/${slug}/photos?limit=200`);
      
      if (response.data.success) {
        setPhotos(response.data.photos);
        preloadAllPhotos(response.data.photos);
      }
    } catch (err) {
      console.error('Error cargando fotos del mural:', err);
    }
  }, [slug]);

  const preloadAllPhotos = (photoList) => {
    photoList.forEach(photo => {
      if (!imagesLoadedRef.current.has(photo.id)) {
        const img = new Image();
        img.src = `${backendUrl}${photo.photoUrl}`;
        img.onload = () => {
          if (isMountedRef.current) {
            imagesLoadedRef.current.set(photo.id, img);
            drawMural();
          }
        };
        img.onerror = () => {
          if (isMountedRef.current) {
            imagesLoadedRef.current.set(photo.id, null);
          }
        };
      }
    });
  };

  const drawMural = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    
    if (!canvas || !ctx || !config) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = projectInfo?.branding?.backgroundColor || '#000000';
    ctx.fillRect(0, 0, width, height);

    if (photos.length === 0) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = 'bold 48px system-ui';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Aún no hay fotos', width / 2, height / 2);
      ctx.font = '24px system-ui';
      ctx.fillText('Las fotos que se suban aparecerán aquí', width / 2, height / 2 + 50);
      return;
    }

    const positions = MuralConfig.getPositionsForTemplate(config.templateType, photos.length);
    
    const photoSize = Math.min(width, height) * 0.08;
    
    positions.forEach((pos, index) => {
      if (index >= photos.length) return;
      
      const photo = photos[index];
      const img = imagesLoadedRef.current.get(photo.id);
      
      const x = pos.x * width;
      const y = pos.y * height;
      
      if (img && img.complete) {
        ctx.save();
        ctx.beginPath();
        
        if (config.templateType === 'circle') {
          ctx.arc(x, y, photoSize / 2, 0, Math.PI * 2);
        } else if (config.templateType === 'star') {
          drawStarPath(ctx, x, y, 5, photoSize / 2, photoSize / 4);
        } else {
          drawHeartPath(ctx, x, y, photoSize);
        }
        
        ctx.clip();
        ctx.drawImage(img, x - photoSize / 2, y - photoSize / 2, photoSize, photoSize);
        ctx.restore();
        
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        if (config.templateType === 'circle') {
          ctx.arc(x, y, photoSize / 2, 0, Math.PI * 2);
        } else if (config.templateType === 'star') {
          drawStarPath(ctx, x, y, 5, photoSize / 2, photoSize / 4);
        } else {
          drawHeartPath(ctx, x, y, photoSize);
        }
        ctx.stroke();
      } else {
        ctx.fillStyle = 'rgba(128, 128, 128, 0.5)';
        ctx.beginPath();
        ctx.arc(x, y, photoSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    if (projectInfo?.branding?.primaryColor) {
      ctx.fillStyle = projectInfo.branding.primaryColor;
      ctx.font = 'bold 32px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText(projectInfo.projectName || '', width / 2, height - 40);
    }

    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '16px system-ui';
    ctx.textAlign = 'right';
    ctx.fillText(`${photos.length} fotos`, width - 20, height - 20);
  }, [config, photos, projectInfo, backendUrl]);

  const drawStarPath = (ctx, cx, cy, spikes, outerRadius, innerRadius) => {
    let rot = Math.PI / 2 * 3;
    let step = Math.PI / spikes;
    
    ctx.moveTo(cx, cy - outerRadius);
    
    for (let i = 0; i < spikes; i++) {
      let x = cx + Math.cos(rot) * outerRadius;
      let y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;
      
      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
  };

  const drawHeartPath = (ctx, x, y, size) => {
    const scale = size / 50;
    ctx.moveTo(x, y + 10 * scale);
    ctx.bezierCurveTo(x, y - 10 * scale, x - 25 * scale, y - 25 * scale, x - 35 * scale, y);
    ctx.bezierCurveTo(x - 45 * scale, y + 20 * scale, x, y + 40 * scale, x, y + 40 * scale);
    ctx.bezierCurveTo(x, y + 40 * scale, x + 45 * scale, y + 20 * scale, x + 35 * scale, y);
    ctx.bezierCurveTo(x + 25 * scale, y - 25 * scale, x, y - 10 * scale, x, y + 10 * scale);
  };

  const handleNewPhoto = useCallback((data) => {
    console.log('📡 [MURAL-SSE] Nueva foto recibida:', data);
    
    if (data.action === 'photo-added' && data.photo) {
      loadPhotos();
    }
  }, [loadPhotos]);

  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const API_URL = process.env.NODE_ENV === 'production'
      ? '/api'
      : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
    const sseUrl = `${API_URL}/mural/${slug}/events`;
    console.log('📡 [MURAL-SSE] Conectando a:', sseUrl);
    
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;
    
    eventSource.onopen = () => {
      console.log('📡 [MURAL-SSE] Conexión establecida');
    };
    
    eventSource.addEventListener('mural:update', (event) => {
      try {
        const data = JSON.parse(event.data);
        handleNewPhoto(data);
      } catch (err) {
        console.error('📡 [MURAL-SSE] Error parseando evento:', err);
      }
    });

    eventSource.addEventListener('new-photo', (event) => {
      try {
        const data = JSON.parse(event.data);
        handleNewPhoto({ action: 'photo-added', photo: data });
      } catch (err) {
        console.error('📡 [MURAL-SSE] Error parseando evento:', err);
      }
    });
    
    eventSource.onerror = (err) => {
      console.error('📡 [MURAL-SSE] Error de conexión:', err);
      eventSource.close();
    };
  }, [slug, handleNewPhoto]);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    resizeTimeoutRef.current = setTimeout(() => {
      if (canvasRef.current && containerRef.current) {
        canvasRef.current.width = containerRef.current.clientWidth;
        canvasRef.current.height = containerRef.current.clientHeight;
        drawMural();
      }
    }, 100);
  }, [drawMural]);

  useEffect(() => {
    loadProjectInfo();
    loadMuralConfig();
    loadPhotos();
    connectSSE();
    
    return () => {
      isMountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [slug, loadProjectInfo, loadMuralConfig, loadPhotos, connectSSE]);

  useEffect(() => {
    if (config && photos.length > 0) {
      drawMural();
    }
  }, [config, photos, drawMural]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch(e.key) {
        case 'F1':
          e.preventDefault();
          setShowInfo(prev => !prev);
          break;
        case 'F11':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Escape':
          setShowInfo(false);
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-xl">Cargando mural...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!config?.isActive) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="text-yellow-500 text-5xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold mb-2">Mural Inactivo</h1>
          <p className="text-gray-400">El mural no está disponible actualmente</p>
          <p className="text-gray-500 mt-2">Contacta al administrador para activarlo</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="w-screen h-screen bg-black flex flex-col relative overflow-hidden"
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ display: 'block' }}
      />

      {showInfo && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-80 p-4 rounded-lg text-white z-20">
          <h3 className="font-bold text-lg mb-2">Información del Mural</h3>
          <p>Template: {TEMPLATE_TYPES[config.templateType]?.icon} {TEMPLATE_TYPES[config.templateType]?.name}</p>
          <p>Fotos: {photos.length}</p>
          <p>Proyecto: {projectInfo?.projectName}</p>
          <p className="text-gray-400 text-sm mt-2">F1 para ocultar</p>
        </div>
      )}

      <div className="absolute bottom-4 right-4 text-white text-opacity-50 text-sm">
        Presiona F1 para info | F11 para pantalla completa
      </div>
    </div>
  );
};

const MuralConfig = {
  getPositionsForTemplate: (templateType, count) => {
    const templateFunctions = {
      heart: generateHeartPositions,
      star: generateStarPositions,
      circle: generateCirclePositions
    };
    
    const generator = templateFunctions[templateType] || generateCirclePositions;
    return generator(Math.min(count, 200));
  }
};

function generateHeartPositions(count) {
  const positions = [];
  const scale = 0.35;

  for (let i = 0; i < count; i++) {
    const t = (i / count) * 2 * Math.PI;
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));

    positions.push({
      x: 0.5 + (x / 16) * scale,
      y: 0.5 + (y / 13) * scale
    });
  }

  return positions;
}

function generateStarPositions(count) {
  const positions = [];
  const outerRadius = 0.42;
  const innerRadius = outerRadius * 0.382;
  const centerX = 0.5;
  const centerY = 0.5;
  const points = 5;
  const positionsPerPoint = Math.floor(count / points);

  for (let point = 0; point < points; point++) {
    const outerAngle = (point * 2 * Math.PI / points) - Math.PI / 2;
    const innerAngle = outerAngle + Math.PI / points;

    for (let i = 0; i < positionsPerPoint; i++) {
      const t = i / positionsPerPoint;
      const angle = outerAngle + (innerAngle - outerAngle) * t;
      const radius = outerRadius - (outerRadius - innerRadius) * Math.abs(t - 0.5) * 2;

      positions.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius
      });
    }
  }

  return positions.slice(0, count);
}

function generateCirclePositions(count) {
  const positions = [];
  const centerX = 0.5;
  const centerY = 0.5;
  const maxRadius = 0.45;

  const rings = [
    { radius: maxRadius * 0.3, count: Math.floor(count * 0.2) },
    { radius: maxRadius * 0.55, count: Math.floor(count * 0.3) },
    { radius: maxRadius * 0.8, count: Math.floor(count * 0.5) }
  ];

  let positionIndex = 0;
  for (const ring of rings) {
    for (let i = 0; i < ring.count && positionIndex < count; i++) {
      const angle = (i / ring.count) * 2 * Math.PI;
      const radiusVariation = 0.85 + Math.random() * 0.3;

      positions.push({
        x: centerX + Math.cos(angle) * ring.radius * radiusVariation,
        y: centerY + Math.sin(angle) * ring.radius * radiusVariation
      });
      positionIndex++;
    }
  }

  return positions.slice(0, count);
}

export default PhotoBoothMural;